/**
 * FE-18: Behavior Tab -- Sessions, Messages, Summaries, Bookmarks, Shares per user per day.
 * DatePresets + 7 KPI cards + TrendLineChart + DonutChart + collapsible table.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchBehaviorMetrics } from "../../api/admin-metrics";
import type { BehaviorMetrics } from "../../api/admin-metrics";
import { Td, Th, tableStyles } from "../../components/DataTable";
import { DatePresets } from "../../components/DatePresets";
import { DonutChart } from "../../components/charts/DonutChart";
import { TrendLineChart } from "../../components/charts/TrendLineChart";
import { t } from "../../i18n";
import * as shared from "../../styles/shared";
import { pct } from "./helpers";

function defaultRange(): { start_date: string; end_date: string } {
  const end = new Date().toISOString().slice(0, 10);
  const start = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  return { start_date: start, end_date: end };
}

export function BehaviorTab(): React.JSX.Element {
  const [range, setRange] = useState(defaultRange);
  const { data, error, loading, load } = useBehaviorData();
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    void load(range.start_date, range.end_date);
  }, [load, range]);

  const trendLines = [
    {
      key: "sessions_per_user",
      color: "#111",
      label: t("behavior.sessions_per_user"),
    },
    {
      key: "messages_per_user",
      color: "#16a34a",
      label: t("behavior.messages_per_user"),
    },
  ] as const;

  const donutData = useMemo(() => {
    if (!data) return [];
    const { message_breakdown } = data;
    return [
      {
        name: t("behavior.human_messages"),
        value: message_breakdown.human,
        color: "#111",
      },
      {
        name: t("behavior.agent_messages"),
        value: message_breakdown.agent,
        color: "#16a34a",
      },
      {
        name: t("behavior.group_messages"),
        value: message_breakdown.group,
        color: "#6366f1",
      },
    ];
  }, [data]);

  return (
    <div>
      <DatePresets value={range} onChange={setRange} />
      {error && <p style={shared.errorBanner}>{error}</p>}
      {loading && !data && (
        <p style={shared.loadingText}>{t("common.loading")}</p>
      )}
      {data && <KPICards data={data} />}
      {data && (
        <section style={shared.section}>
          <TrendLineChart data={data.rows} lines={trendLines} xKey="date" />
        </section>
      )}
      {donutData.length > 0 && (
        <section style={shared.section}>
          <h3 style={shared.sectionTitle}>{t("behavior.message_breakdown")}</h3>
          <DonutChart data={donutData} size={240} />
        </section>
      )}
      <section style={shared.section}>
        <button
          type="button"
          onClick={() => setShowTable(!showTable)}
          style={s.toggleBtn}
        >
          {showTable ? t("common.hide_details") : t("common.show_details")}
        </button>
        {showTable && data && <DailyTable rows={data.rows} />}
      </section>
    </div>
  );
}

// --- KPI Cards ---

function KPICards({ data }: { data: BehaviorMetrics }): React.JSX.Element {
  const { totals } = data;
  return (
    <section style={shared.section}>
      <div style={shared.cardGrid}>
        <Card
          label={t("behavior.sessions_per_user")}
          value={totals.sessions_per_user.toFixed(1)}
        />
        <Card
          label={t("behavior.messages_per_user")}
          value={totals.messages_per_user.toFixed(1)}
        />
        <Card
          label={t("behavior.summaries_per_user")}
          value={totals.summaries_per_user.toFixed(1)}
        />
        <Card
          label={t("behavior.bookmarks_per_user")}
          value={totals.bookmarks_per_user.toFixed(1)}
        />
        <Card
          label={t("behavior.shares_per_user")}
          value={totals.shares_per_user.toFixed(1)}
        />
        <Card
          label={t("behavior.agent_usage_rate")}
          value={pct(totals.agent_usage_rate)}
        />
        <Card
          label={t("behavior.retention_rate")}
          value={pct(totals.retention_rate)}
        />
      </div>
    </section>
  );
}

function Card({
  label,
  value,
}: { label: string; value: string }): React.JSX.Element {
  return (
    <div style={{ ...shared.card, padding: 20 }}>
      <span style={s.cardLabel}>{label}</span>
      <span style={shared.bigNumber}>{value}</span>
    </div>
  );
}

// --- Daily Table ---

function DailyTable({
  rows,
}: {
  rows: ReadonlyArray<BehaviorMetrics["rows"][number]>;
}): React.JSX.Element {
  return (
    <div style={{ marginTop: 12 }}>
      <h3 style={shared.sectionTitle}>{t("behavior.daily_breakdown")}</h3>
      <table style={tableStyles.table}>
        <thead>
          <tr>
            <Th>{t("behavior.col_date")}</Th>
            <Th>{t("behavior.col_sessions")}</Th>
            <Th>{t("behavior.col_msgs")}</Th>
            <Th>{t("behavior.col_summaries")}</Th>
            <Th>{t("behavior.col_bookmarks")}</Th>
            <Th>{t("behavior.col_shares")}</Th>
            <Th>{t("behavior.col_agent_rate")}</Th>
            <Th>{t("behavior.col_retention")}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.date}>
              <Td>{row.date}</Td>
              <Td mono>{row.sessions_per_user.toFixed(1)}</Td>
              <Td mono>{row.messages_per_user.toFixed(1)}</Td>
              <Td mono>{row.summaries_per_user.toFixed(1)}</Td>
              <Td mono>{row.bookmarks_per_user.toFixed(1)}</Td>
              <Td mono>{row.shares_per_user.toFixed(1)}</Td>
              <Td mono>{pct(row.agent_usage_rate)}</Td>
              <Td mono>{pct(row.retention_rate)}</Td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <Td colSpan={8}>{t("common.no_data_for_period")}</Td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// --- Hooks ---

function useBehaviorData(): {
  data: BehaviorMetrics | null;
  error: string | null;
  loading: boolean;
  load: (start: string, end: string) => Promise<void>;
} {
  const [data, setData] = useState<BehaviorMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (start: string, end: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        setData(
          await fetchBehaviorMetrics({ start_date: start, end_date: end }),
        );
      } catch (err: unknown) {
        setError(
          err instanceof Error ? err.message : t("common.failed_to_load"),
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, error, loading, load };
}

// --- Styles ---

const s: Record<string, React.CSSProperties> = {
  cardLabel: {
    display: "block",
    fontSize: 13,
    color: "var(--color-text-secondary)",
    fontWeight: 500,
    marginBottom: 4,
  },
  toggleBtn: {
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
    cursor: "pointer",
  },
};
